import { alpha, createTheme } from "@mui/material";
import { stateBlue, stateGray, stateGreen, stateLightGray, stateOrange, stateRed } from "ui/Scripts/source/models/consts";

declare module "@mui/material/styles" {
    // eslint-disable-next-line no-unused-vars
    interface BreakpointOverrides {
        xs: true;
        sm: true;
        md: true;
        lg: true;
        xl: true;
        xxl: true;
    }

    // eslint-disable-next-line no-unused-vars
    interface Palette {
        stateLightGray: Palette["primary"];
        stateGray: Palette["primary"];
        stateBlue: Palette["primary"];
        stateRed: Palette["primary"];
        stateOrange: Palette["primary"];
        stateGreen: Palette["primary"];
    }

    // eslint-disable-next-line no-unused-vars
    interface PaletteOptions {
        stateLightGray?: PaletteOptions["primary"];
        stateGray?: PaletteOptions["primary"];
        stateBlue?: PaletteOptions["primary"];
        stateRed?: PaletteOptions["primary"];
        stateOrange?: PaletteOptions["primary"];
        stateGreen?: PaletteOptions["primary"];
    }
}

export const initTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            xxl: 1736
        },
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
    },
    palette: {
        // mode: "dark",
        // background: {
        //     default: "#040d19",
        //     paper: "#071426",
        // },
        primary: {
            main: "#2856B7",
        },
        secondary: {
            main: "#156DEE",
        },
        text: {
            disabled: "rgba(0, 0, 0, .6)",
        },
        action: {
            selected: alpha("#2856B7", 0.12),
            selectedOpacity: 0.12,
        },
        background: {
            default: "#e9ecf0",
        },
        error: {
            main: "#ff7878",
            contrastText: "#fff",
        },
        success: {
            main: "#4caf50",
        },
        info: {
            main: "#03a9f4",
        },
        warning: {
            main: "#ff9800",
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                "body": {
                    boxSizing: "border-box",
                    fontFamily: "'Inter', sans-serif !important",
                    fontSize: "14px",
                    height: "100%",
                    lineHeight: "1.42857143",
                    paddingTop: "120px !important",
                    backgroundColor: `${theme.palette.background.default} !important`,
                },
                "*": {
                    "scrollbarWidth": "thin",
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                },
            }
            ),
        },
        MuiTypography: {
            defaultProps: {
                variant: "body2",
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: ".5rem",
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: ({ theme }) => ({
                    background: theme.palette.action.hover,
                    borderBottom: "1px solid",
                    borderColor: theme.palette.divider,
                    fontSize: "1rem",
                    color: theme.palette.text.secondary,
                    padding: "9px 1.5rem",
                }),
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.getContrastText(theme.palette.background.default),
                    fontSize: "1rem",
                    fontWeight: 600,
                }),
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: "1rem 1.5rem !important",
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: "16px 24px",
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginBottom: "0px !important",
                },
            },
            defaultProps: {
                slotProps: {
                    typography: {
                        variant: "body2",
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: ({ theme }) => ({
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    background: theme.palette.action.hover,
                }),
                content: {
                    width: "100%",
                }
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    "textTransform": "none",
                    "fontSize": ".875rem",
                    "minHeight": "44px",
                    ":focus": {
                        outline: "none",
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                outlined: ({ theme }) => ({
                    backgroundColor: theme.palette.background.paper,
                }),
                root: {
                    borderRadius: "8px",
                    textTransform: "none",
                    outline: "none !important",
                },
                sizeMedium: {
                    padding: "7px 16px",
                }
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    ":focus": {
                        outline: "none",
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme }) => ({
                    background: theme.palette.background.paper,
                }),
            },
        },
        MuiInputBase: {
            defaultProps: {
                size: "small",
                fullWidth: true,
            },
        },
        MuiSelect: {
            defaultProps: {
                displayEmpty: true,
                size: "small",
                fullWidth: true,
                MenuProps: {
                    slotProps: {
                        paper: {
                            sx: {
                                maxHeight: "374px",
                            },
                        },
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    maxHeight: "42px",
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    ":focus": {
                        outline: "none",
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: "42px",
                }),
            },
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiLink: {
            defaultProps: {
                textAlign: "left",
            },
            styleOverrides: {
                button: {
                    outline: "none !important",
                },
            },
        },
        MuiCircularProgress: {
            defaultProps: {
                color: "primary",
            },
        },
    },
});

const getAppTheme = (initTheme: any) => {
    return {
        palette: {
            // Custom colors
            stateLightGray: initTheme.palette.augmentColor({
                color: {
                    main: stateLightGray,
                    fontColor: "#ffffff"
                },
                name: "stateLightGray",
            }),
            stateGray: initTheme.palette.augmentColor({
                color: {
                    main: stateGray,
                    fontColor: "#ffffff"
                },
                name: "stateGray",
            }),
            stateBlue: initTheme.palette.augmentColor({
                color: {
                    main: stateBlue,
                    fontColor: "#ffffff"
                },
                name: "stateBlue",
            }),
            stateRed: initTheme.palette.augmentColor({
                color: {
                    main: stateRed,
                    fontColor: "#ffffff"
                },
                name: "stateRed",
            }),
            stateOrange: initTheme.palette.augmentColor({
                color: {
                    main: stateOrange,
                    fontColor: "#ffffff"
                },
                name: "stateOrange",
            }),
            stateGreen: initTheme.palette.augmentColor({
                color: {
                    main: stateGreen,
                    fontColor: "#ffffff"
                },
                name: "stateGreen",
            }),
        }
    };
};

export const appTheme = createTheme(getAppTheme(initTheme), initTheme);